// Multi-word terms must come before any single-word substring they contain.
// e.g. "striped bass" before "bass", "red drum" before "drum".
const SPECIES_TERMS = [
  "speckled trout",
  "striped bass",
  "black drum",
  "red drum",
  "redfish",
  "snook",
  "tarpon",
  "flounder",
  "sheepshead",
  "mackerel",
  "catfish",
  "trout",
  "drum",
  "bass",
  "jack",
];

const SMALL_NUMS = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const TENS = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

function toTitleCase(value) {
  if (!value) return null;
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function normalize(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[，]/g, ",")
    .replace(/[”″]/g, '"')
    .replace(/\blenght\b/g, "length")
    .replace(/\s+/g, " ")
    .trim();
}

function parseSpokenNumber(value) {
  if (!value) return null;
  const raw = value.trim().toLowerCase();
  if (/^\d+(?:\.\d+)?$/.test(raw)) return parseFloat(raw);

  const words = raw
    .replace(/-/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => w !== "and");

  let total = 0;
  let current = 0;
  let decimalMode = false;
  let decimalDigits = "";

  for (const word of words) {
    if (word === "point") {
      decimalMode = true;
      continue;
    }

    const small = SMALL_NUMS[word];
    const tens = TENS[word];

    if (decimalMode) {
      if (small != null) {
        decimalDigits += String(small);
      } else if (/^\d$/.test(word)) {
        decimalDigits += word;
      }
      continue;
    }

    if (small != null) {
      current += small;
      continue;
    }
    if (tens != null) {
      current += tens;
      continue;
    }
    if (/^\d+(?:\.\d+)?$/.test(word)) {
      current += parseFloat(word);
      continue;
    }
    if (word === "hundred") {
      current = current === 0 ? 100 : current * 100;
      continue;
    }
    if (word === "thousand") {
      total += (current === 0 ? 1 : current) * 1000;
      current = 0;
      continue;
    }
  }

  const integerPart = total + current;
  if (integerPart === 0 && !decimalDigits) return null;
  if (!decimalDigits) return integerPart;
  return parseFloat(`${integerPart}.${decimalDigits}`);
}

function findSpecies(text) {
  const term = SPECIES_TERMS.find((item) => text.includes(item));
  if (term) return toTitleCase(term);

  const firstChunk = text.split(",")[0]?.trim();
  if (!firstChunk) return null;
  const cleaned = firstChunk.replace(/\b(i|caught|a|an|the)\b/g, "").trim();
  if (!cleaned) return null;
  return toTitleCase(cleaned.split(" ").slice(0, 2).join(" "));
}

function extractValueBeforeUnit(text, unitPattern) {
  const match = text.match(new RegExp(`([a-z0-9\\s.-]{1,30})\\s*(?:${unitPattern})`, "i"));
  if (!match) return null;
  return parseSpokenNumber(match[1]);
}

function extractValueFromPatterns(text, patterns) {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match?.[1]) continue;
    const parsed = parseSpokenNumber(match[1]);
    if (parsed != null) return parsed;
  }
  return null;
}

function findLengthIn(text) {
  return extractValueFromPatterns(text, [
    /(?:length|measured|measures|about|around|at)\s+(?:(?:is|was)\s+)?([a-z0-9\s.-]{1,24})\s*(?:inches|inch|inchs|in\b|")/i,
    /\b(\d+(?:\.\d+)?)\s*(?:inches|inch|inchs|in\b|")/i,
    /([a-z0-9\s.-]{1,24})\s*(?:inches|inch|inchs|")\b/i,
  ]);
}

function findWeightLbs(text) {
  return extractValueFromPatterns(text, [
    /(?:weight|weighed|weighs|at|about|around)\s+(?:(?:is|was)\s+)?([a-z0-9\s.-]{1,24})\s*(?:pounds|pound|lbs|lb)\b/i,
    /\b(\d+(?:\.\d+)?)\s*(?:pounds|pound|lbs|lb)\b/i,
    /([a-z0-9\s.-]{1,24})\s*(?:pounds|pound|lbs|lb)\b/i,
  ]);
}

function findLure(text) {
  const lureMatch = text.match(/(?:lure(?: was)?|bait(?: was)?|on|using|with)\s+([a-z0-9\s-]{2,40})/i);
  if (lureMatch?.[1]) {
    const cleaned = lureMatch[1]
      .replace(/\bfor\b.*$/, "")
      .replace(/\bthat'?s all\b.*$/, "")
      .trim();
    return toTitleCase(cleaned.split(" ").slice(0, 4).join(" "));
  }

  const chunks = text.split(",").map((c) => c.trim()).filter(Boolean);
  if (chunks.length >= 4) return toTitleCase(chunks[chunks.length - 1]);
  return null;
}

export function extractCatchFromTranscript(rawTranscript) {
  const transcript = normalize(rawTranscript);
  if (!transcript) {
    return {
      transcript: "",
      species: null,
      lengthIn: null,
      weightLbs: null,
      lure: null,
    };
  }

  const lengthIn = findLengthIn(transcript) ?? extractValueBeforeUnit(transcript, 'inches|inch|inchs|"');
  const weightLbs = findWeightLbs(transcript) ?? extractValueBeforeUnit(transcript, "pounds|pound|lbs|lb");

  return {
    transcript,
    species: findSpecies(transcript),
    lengthIn,
    weightLbs,
    lure: findLure(transcript),
  };
}
